import { isDesktop } from '../../lib/platform.ts'

/**
 * Ponte com as webviews nativas do Orbind Desktop.
 *
 * No desktop cada aplicação externa é uma webview de verdade, filha da janela
 * principal e posicionada sobre a área de conteúdo — não um `<iframe>`. É o que
 * faz sistemas corporativos funcionarem: para eles, a página é a principal, e as
 * regras que barram enquadramento (cabeçalho, cookie `SameSite`, dependência da
 * janela de cima) simplesmente não se aplicam.
 *
 * A chamada usa `__TAURI_INTERNALS__` direto, sem `@tauri-apps/api`: o mesmo
 * bundle roda no navegador, e uma dependência que só existe no desktop viraria
 * peso morto — ou erro de importação — na web.
 */

type Invoke = (comando: string, argumentos?: Record<string, unknown>) => Promise<unknown>

function ponte(): Invoke | null {
  if (!isDesktop()) return null
  const w = window as unknown as { __TAURI_INTERNALS__?: { invoke?: Invoke } }
  return w.__TAURI_INTERNALS__?.invoke ?? null
}

/** Retângulo onde a webview deve aparecer, em coordenadas da janela. */
export type Area = { x: number; y: number; largura: number; altura: number }

async function chamar(comando: string, argumentos?: Record<string, unknown>) {
  const invoke = ponte()
  if (!invoke) return null
  try {
    return await invoke(comando, argumentos)
  } catch (erro) {
    console.error(`[aplicações] ${comando} falhou:`, erro)
    throw erro
  }
}

export const desktopApps = {
  /** Cria a webview (com o zoom de abertura), ou reposiciona se já existir. */
  abrir: (id: string, url: string, area: Area, zoom = 100) =>
    chamar('app_abrir', { id, url, ...area, zoom }),

  /** Acompanha a área quando a janela ou o explorer mudam de tamanho. */
  mover: (id: string, area: Area) => chamar('app_mover', { id, ...area }),

  /** Troca o zoom (%) de uma aplicação aberta. */
  zoom: (id: string, zoom: number) => chamar('app_zoom', { id, zoom }),

  /** Tira da vista sem destruir — a sessão do site continua viva. */
  esconder: (id: string) => chamar('app_esconder', { id }),

  /** Fecha de verdade. */
  fechar: (id: string) => chamar('app_fechar', { id }),

  /** Abre a pasta de Downloads do Windows — onde caem os downloads das aplicações. */
  abrirDownloads: () => chamar('downloads_abrir'),

  /** Downloads interceptados das aplicações, do mais novo para o mais velho. */
  listarDownloads: async () =>
    ((await chamar('downloads_listar')) as
      | { id: number; app: string; arquivo: string; caminho: string; status: string; quando_ms: number }[]
      | null) ?? [],

  /** Abre o arquivo de um download registrado. */
  abrirDownload: (id: number) => chamar('download_abrir', { id }),

  /** Abre o Explorer com o arquivo selecionado. */
  revelarDownload: (id: number) => chamar('download_revelar', { id }),

  /** Abre OUTRA janela completa do Orbind (2026-08-31). */
  novaJanela: () => chamar('janela_nova'),

  recarregar: (id: string) => chamar('app_recarregar', { id }),

  /** Quais continuam abertas — usado para reconciliar depois de um recarregamento do shell. */
  abertas: async () => ((await chamar('app_abertas')) as string[] | null) ?? [],

  /**
   * Preenche o login da aplicação e envia.
   *
   * Só possível aqui: o script roda dentro da página do site, com acesso pleno
   * ao DOM dela. Nenhuma página web consegue isso num site de outra origem.
   */
  autologin: (id: string, usuario: string, senha: string, enviar = true) =>
    chamar('app_autologin', { id, usuario, senha, enviar }),
}

/**
 * Converte a posição de um elemento na área da webview.
 *
 * O Tauri trabalha em coordenadas lógicas da janela, e a webview principal ocupa
 * a janela inteira — então o retângulo do elemento na viewport já é a posição
 * certa, sem conversão de escala.
 */
export function areaDoElemento(elemento: HTMLElement): Area {
  const r = elemento.getBoundingClientRect()
  return {
    x: Math.round(r.left),
    y: Math.round(r.top),
    largura: Math.max(1, Math.round(r.width)),
    altura: Math.max(1, Math.round(r.height)),
  }
}
